import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Search, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Clock,
  Image,
  Link,
  FileText,
  Zap,
  TrendingUp,
  Eye,
  RefreshCw,
  Shield,
  Activity,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { enhancedSitemapService } from '@/services/enhancedSitemapService';
import { seoFetchService, CrawlResult } from '@/services/seoFetchService';
import SEOMetaAuditor from './SEOMetaAuditor';
import ImageAltOptimizer from './ImageAltOptimizer';
import InternalLinkOptimizer from './InternalLinkOptimizer';
import CrawlOptimizer from './CrawlOptimizer';
import ContentAnalyzer from './ContentAnalyzer';
import MetaOptimizer from './MetaOptimizer';
import CoreWebVitalsMonitor from './CoreWebVitalsMonitor';
import MobileOptimizationTool from './MobileOptimizationTool';
import AdvancedAnalyticsDashboard from '@/components/analytics/AdvancedAnalyticsDashboard';
import WeeklySEOUXReport from '@/components/analytics/WeeklySEOUXReport';
import PostHogDashboard from '@/components/analytics/PostHogDashboard';

interface SEOIssue {
  id: string;
  type: 'error' | 'warning' | 'info';
  category: 'meta' | 'images' | 'links' | 'performance' | 'structure';
  title: string;
  description: string;
  page?: string;
  impact: 'high' | 'medium' | 'low';
  fix?: string;
}

interface SEOAuditResults {
  score: number;
  issues: SEOIssue[];
  totalPages: number;
  lastUpdated: string;
  recommendations: string[];
  analyzedUrl: string;
  crawlData?: CrawlResult;
  crawlProgress?: {
    processed: number;
    total: number;
    successful: number;
    failed: number;
  };
}

const SEOAuditTool = () => {
  const [auditResults, setAuditResults] = useState<SEOAuditResults | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [targetUrl, setTargetUrl] = useState('/');
  const [analysisMode, setAnalysisMode] = useState<'single' | 'crawl'>('single');
  const [maxPages, setMaxPages] = useState(50);
  const [crawlProgress, setCrawlProgress] = useState<{processed: number; total: number} | null>(null);

  // Predefined SeniorThrive pages to analyze
  const seniorThrivePages = [
    { name: 'Home Page', url: '/' },
    { name: 'Get Started', url: '/get-started' },
    { name: 'Services', url: '/services' },
    { name: 'About', url: '/about' },
    { name: 'Pricing', url: '/pricing' },
    { name: 'Professional Network', url: '/professional-network' },
    { name: 'Contact', url: '/contact' },
    { name: 'Blog', url: '/blog' },
    { name: 'Privacy Policy', url: '/privacy' },
    { name: 'Terms of Service', url: '/terms' }
  ];

  // Analyze a document for SEO issues
  const analyzeDocument = useCallback(async (doc: Document, pageUrl: string) => {
    const issues: SEOIssue[] = [];
    let score = 100;

    try {
      // Check for multiple meta description tags (Ahrefs issue)
      const metaDescTags = doc.querySelectorAll('meta[name="description"]');
      if (metaDescTags.length > 1) {
        issues.push({
          id: 'multiple-meta-desc',
          type: 'error',
          category: 'meta',
          title: 'Multiple Meta Description Tags',
          description: `Found ${metaDescTags.length} meta description tags. Should have exactly one.`,
          impact: 'high',
          fix: 'Remove duplicate meta description tags'
        });
        score -= 20;
      }

      // Check meta description
      const metaDescription = doc.querySelector('meta[name="description"]')?.getAttribute('content');
      if (!metaDescription) {
        issues.push({
          id: 'meta-desc-missing',
          type: 'error',
          category: 'meta',
          title: 'Missing Meta Description',
          description: 'This page is missing a meta description tag.',
          impact: 'high',
          fix: 'Add a meta description tag with 150-160 characters'
        });
        score -= 15;
      } else if (metaDescription.length < 120) {
        issues.push({
          id: 'meta-desc-short',
          type: 'warning',
          category: 'meta',
          title: 'Meta Description Too Short',
          description: `Meta description is ${metaDescription.length} characters. Should be at least 120 characters.`,
          impact: 'medium',
          fix: 'Expand meta description to 150-160 characters'
        });
        score -= 8;
      } else if (metaDescription.length > 160) {
        issues.push({
          id: 'meta-desc-long',
          type: 'warning',
          category: 'meta',
          title: 'Meta Description Too Long',
          description: `Meta description is ${metaDescription.length} characters. Should be under 160 characters.`,
          impact: 'medium',
          fix: 'Shorten meta description to 150-160 characters'
        });
        score -= 5;
      }

      // Check title tag
      const title = doc.title;
      if (!title) {
        issues.push({
          id: 'title-missing',
          type: 'error',
          category: 'meta',
          title: 'Missing Title Tag',
          description: 'This page is missing a title tag.',
          impact: 'high',
          fix: 'Add a descriptive title tag with target keywords'
        });
        score -= 20;
      } else if (title.length > 60) {
        issues.push({
          id: 'title-too-long',
          type: 'warning',
          category: 'meta',
          title: 'Title Too Long',
          description: `Title is ${title.length} characters. Should be under 60 characters.`,
          impact: 'medium',
          fix: 'Shorten title to under 60 characters'
        });
        score -= 8;
      } else if (title.length < 30) {
        issues.push({
          id: 'title-too-short',
          type: 'warning',
          category: 'meta',
          title: 'Title Too Short',
          description: `Title is ${title.length} characters. Should be at least 30 characters.`,
          impact: 'medium',
          fix: 'Expand title to 30-60 characters'
        });
        score -= 5;
      }

      // Check H1 tags
      const h1Tags = doc.querySelectorAll('h1');
      if (h1Tags.length === 0) {
        issues.push({
          id: 'h1-missing',
          type: 'error',
          category: 'structure',
          title: 'H1 Tag Missing or Empty',
          description: 'This page has no H1 tag.',
          impact: 'high',
          fix: 'Add one H1 tag with the main page topic'
        });
        score -= 15;
      } else if (h1Tags.length > 1) {
        issues.push({
          id: 'multiple-h1',
          type: 'error',
          category: 'structure',
          title: 'Multiple H1 Tags',
          description: `Found ${h1Tags.length} H1 tags. Should have exactly one.`,
          impact: 'high',
          fix: 'Keep only one H1 tag per page'
        });
        score -= 15;
      } else if (h1Tags[0].textContent?.trim() === '') {
        issues.push({
          id: 'h1-empty',
          type: 'error',
          category: 'structure',
          title: 'H1 Tag Empty',
          description: 'H1 tag exists but is empty.',
          impact: 'high',
          fix: 'Add meaningful content to H1 tag'
        });
        score -= 15;
      }

      // Check images
      const images = doc.querySelectorAll('img');
      const imagesWithoutAlt = Array.from(images).filter(img => !img.getAttribute('alt'));

      if (imagesWithoutAlt.length > 0) {
        issues.push({
          id: 'images-no-alt',
          type: 'warning',
          category: 'images',
          title: 'Images Missing Alt Text',
          description: `${imagesWithoutAlt.length} images are missing alt text.`,
          impact: 'medium',
          fix: 'Add descriptive alt text to all images'
        });
        score -= Math.min(15, imagesWithoutAlt.length * 2);
      }

      // Check Open Graph tags
      const ogTitle = doc.querySelector('meta[property="og:title"]');
      const ogDescription = doc.querySelector('meta[property="og:description"]');
      const ogImage = doc.querySelector('meta[property="og:image"]');

      if (!ogTitle || !ogDescription || !ogImage) {
        issues.push({
          id: 'incomplete-og-tags',
          type: 'warning',
          category: 'meta',
          title: 'Open Graph Tags Incomplete',
          description: 'Missing essential Open Graph tags for social media sharing.',
          impact: 'medium',
          fix: 'Add og:title, og:description, and og:image tags'
        });
        score -= 8;
      }

      // Check canonical URL
      const canonical = doc.querySelector('link[rel="canonical"]');
      if (!canonical) {
        issues.push({
          id: 'canonical-missing',
          type: 'warning',
          category: 'meta',
          title: 'Missing Canonical URL',
          description: 'Page lacks a canonical URL tag.',
          impact: 'medium',
          fix: 'Add canonical URL to prevent duplicate content issues'
        });
        score -= 8;
      }

      // Check word count (estimate from body)
      const bodyText = doc.body?.innerText || '';
      const wordCount = bodyText.split(/\s+/).filter(word => word.length > 0).length;
      
      if (wordCount < 300) {
        issues.push({
          id: 'low-word-count',
          type: 'warning',
          category: 'structure',
          title: 'Low Word Count',
          description: `Page has only ${wordCount} words. Should have at least 300 words for better SEO.`,
          impact: 'medium',
          fix: 'Add more valuable content to reach 300+ words'
        });
        score -= 8;
      }

      return {
        score: Math.max(0, score),
        issues,
        analyzedUrl: pageUrl
      };

    } catch (error) {
      console.error('Document analysis error:', error);
      throw error;
    }
  }, []);

  // Function to analyze a specific URL by fetching it
  const analyzeUrlByFetch = useCallback(async (url: string) => {
    try {
      const fullUrl = url.startsWith('http')
        ? url
        : `https://seniorthrive.com${url.startsWith('/') ? '' : '/'}${url}`;

      const { html, url: finalUrl } = await seoFetchService.fetchHtml(fullUrl);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      return await analyzeDocument(doc, finalUrl);
    } catch (error) {
      console.error('Failed to analyze URL:', url, error);
      throw new Error(`Cannot analyze ${url}: ${error}`);
    }
  }, [analyzeDocument]);

  // Analyze current admin page
  const analyzeCurrentPage = useCallback(async () => {
    try {
      return await analyzeDocument(document, window.location.href);
    } catch (error) {
      console.error('Current page analysis error:', error);
      throw error;
    }
  }, [analyzeDocument]);

  // Generate recommendations based on issues
  const generateRecommendations = (issues: SEOIssue[]): string[] => {
    const recommendations: string[] = [];
    
    if (issues.some(i => i.category === 'meta')) {
      recommendations.push('Optimize meta tags for better search visibility');
    }
    if (issues.some(i => i.category === 'images')) {
      recommendations.push('Add descriptive alt text to improve accessibility and SEO');
    }
    if (issues.some(i => i.category === 'structure')) {
      recommendations.push('Fix heading structure for better content hierarchy');
    }
    if (issues.some(i => i.category === 'performance')) {
      recommendations.push('Improve page load speed for better user experience');
    }
    if (issues.some(i => i.category === 'links')) {
      recommendations.push('Enhance internal linking strategy');
    }
    
    if (recommendations.length === 0) {
      recommendations.push('Great job! This page is well optimized for SEO');
    }
    
    return recommendations;
  };

  // Run SEO audit based on current mode
  const runSEOAudit = async () => {
    setIsRunning(true);
    setCrawlProgress(null);
    
    try {
      let analysisResult: { score: number; issues: SEOIssue[]; analyzedUrl: string } | null = null;
      let crawlData: CrawlResult | undefined;

      if (analysisMode === 'single') {
        analysisResult = await analyzeUrlByFetch(targetUrl);
        toast.success(`Analysis completed for ${targetUrl}!`);
      } else {
        // Full site crawl
        toast.info(`Starting comprehensive site crawl (up to ${maxPages} pages)...`);
        
        crawlData = await seoFetchService.crawlFullSite(maxPages);
        setCrawlProgress({ processed: crawlData.processed, total: crawlData.total });
        
        // Analyze all successful crawl results
        let totalScore = 0;
        const allIssues: SEOIssue[] = [];
        let processedCount = 0;

        for (const result of crawlData.results) {
          if (result.html && !result.error) {
            try {
              const parser = new DOMParser();
              const doc = parser.parseFromString(result.html, 'text/html');
              const pageAnalysis = await analyzeDocument(doc, result.url);
              
              totalScore += pageAnalysis.score;
              processedCount++;
              
              // Attach page URL to each issue for context
              allIssues.push(...pageAnalysis.issues.map((i) => ({ 
                ...i, 
                page: result.url.replace('https://seniorthrive.com', '') || '/' 
              })));
            } catch (e) {
              console.warn('Failed to analyze crawled page:', result.url, e);
            }
          }
        }

        const avgScore = processedCount > 0 ? Math.round(totalScore / processedCount) : 0;
        analysisResult = { 
          score: avgScore, 
          issues: allIssues, 
          analyzedUrl: `Full site crawl (${crawlData.successful}/${crawlData.processed} pages)` 
        };
        
        toast.success(`Site crawl completed: analyzed ${crawlData.successful} pages successfully`);
      }
      
      // Get sitemap statistics
      const sitemapStats = await enhancedSitemapService.getSitemapStats();

      const results: SEOAuditResults = {
        score: analysisResult?.score || 0,
        issues: analysisResult?.issues || [],
        totalPages: crawlData?.total || sitemapStats.totalUrls,
        lastUpdated: new Date().toLocaleString(),
        recommendations: generateRecommendations(analysisResult?.issues || []),
        analyzedUrl: analysisMode === 'single' ? (analysisResult?.analyzedUrl || targetUrl) : 'Full site crawl',
        crawlData,
        crawlProgress: crawlData ? {
          processed: crawlData.processed,
          total: crawlData.total,
          successful: crawlData.successful,
          failed: crawlData.failed
        } : undefined
      };

      setAuditResults(results);

      const criticalIssues = (analysisResult?.issues || []).filter(i => i.type === 'error').length;
      if (criticalIssues > 0) {
        toast.error(`Found ${criticalIssues} critical SEO issues across the site`);
      } else if ((analysisResult?.issues || []).length === 0) {
        toast.success('No SEO issues found across the analyzed pages!');
      }

    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error occurred';
      toast.error(`Failed to analyze: ${errorMessage}`);
      console.error('SEO audit error:', error);
      
      // Set empty results to show the UI
      setAuditResults({
        score: 0,
        issues: [],
        totalPages: 0,
        lastUpdated: new Date().toLocaleString(),
        recommendations: ['Unable to complete analysis. Please try again.'],
        analyzedUrl: 'Error occurred',
        crawlData: undefined
      });
    } finally {
      setIsRunning(false);
      setCrawlProgress(null);
    }
  };

  // Generate sitemap
  const generateSitemap = async () => {
    try {
      toast.info('Generating enhanced sitemap...');
      await enhancedSitemapService.saveSitemapToFile();
      toast.success('Sitemap generated! Check console for XML content.');
    } catch (error) {
      toast.error('Failed to generate sitemap');
      console.error('Sitemap generation error:', error);
    }
  };

  // Navigate to specific tab based on issue category
  const navigateToCategory = (category: string) => {
    setActiveTab(category);
  };

  // Get icon for issue type
  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'error': return <XCircle className="h-4 w-4 text-destructive" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'info': return <CheckCircle className="h-4 w-4 text-blue-600" />;
      default: return <CheckCircle className="h-4 w-4" />;
    }
  };

  // Get badge color for impact
  const getImpactBadge = (impact: string) => {
    switch (impact) {
      case 'high': return <Badge variant="destructive">High Impact</Badge>;
      case 'medium': return <Badge variant="secondary">Medium Impact</Badge>;
      case 'low': return <Badge variant="outline">Low Impact</Badge>;
      default: return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const issuesByCategory = auditResults?.issues.reduce((acc, issue) => {
    if (!acc[issue.category]) acc[issue.category] = [];
    acc[issue.category].push(issue);
    return acc;
  }, {} as Record<string, SEOIssue[]>) || {};

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            SeniorThrive SEO Analyzer
          </CardTitle>
          <CardDescription>
            Analyze any SeniorThrive page for SEO issues and optimization opportunities.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Analysis Target Selection */}
          <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
            <div className="flex items-center gap-4">
              <Globe className="h-5 w-5" />
              <h3 className="font-semibold">Choose page to analyze:</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select value={analysisMode} onValueChange={(value: 'single' | 'crawl') => setAnalysisMode(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Analysis mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single Page</SelectItem>
                  <SelectItem value="crawl">Crawl Site (sitemap)</SelectItem>
                </SelectContent>
              </Select>

              {analysisMode === 'single' && (
                <>
                  <Select value={targetUrl} onValueChange={setTargetUrl}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a page" />
                    </SelectTrigger>
                    <SelectContent>
                      {seniorThrivePages.map(page => (
                        <SelectItem key={page.url} value={page.url}>
                          {page.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    placeholder="Or enter custom path or URL (e.g. /custom-page or https://seniorthrive.com/page)"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                  />
                </>
              )}

              {analysisMode === 'crawl' && (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground">
                    Will comprehensively analyze pages from the sitemap.
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Max pages to crawl:</label>
                    <Select value={maxPages.toString()} onValueChange={(value) => setMaxPages(parseInt(value))}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20 pages</SelectItem>
                        <SelectItem value="50">50 pages</SelectItem>
                        <SelectItem value="100">100 pages</SelectItem>
                        <SelectItem value="200">200 pages (slow)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {auditResults && (
              <div className="text-sm text-muted-foreground">
                Last analyzed: {auditResults.analyzedUrl} at {auditResults.lastUpdated}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={runSEOAudit}
              disabled={isRunning}
              className="flex-1 min-w-[180px]"
            >
              {isRunning && crawlProgress ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Crawling... {crawlProgress.processed}/{crawlProgress.total}
                </>
              ) : isRunning ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  {analysisMode === 'single' ? `Analyze ${targetUrl || 'Page'}` : `Start Full Site Crawl (${maxPages} pages)`}
                </>
              )}
            </Button>
            
            <Button 
              onClick={generateSitemap}
              variant="outline"
            >
              <FileText className="mr-2 h-4 w-4" />
              Generate Sitemap
            </Button>
          </div>

          {auditResults && (
            <div className="space-y-4 pt-4">
              {/* SEO Score */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">SEO Score</h3>
                  <span className="text-2xl font-bold text-primary">{auditResults.score}/100</span>
                </div>
                <Progress value={auditResults.score} className="h-3" />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Analyzed: {auditResults.analyzedUrl}</span>
                  <span>{auditResults.totalPages} total pages in sitemap</span>
                </div>
                {auditResults.crawlProgress && (
                  <div className="text-sm text-muted-foreground">
                    Crawl status: {auditResults.crawlProgress.successful} successful, {auditResults.crawlProgress.failed} failed
                  </div>
                )}
              </div>

              {/* Issue Summary - Clickable Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigateToCategory('meta')}>
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-destructive" />
                    <div>
                      <div className="font-semibold">
                        {auditResults.issues.filter(i => i.type === 'error').length}
                      </div>
                      <div className="text-sm text-muted-foreground">Errors</div>
                      <div className="text-xs text-primary mt-1">Click to view →</div>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigateToCategory('structure')}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <div>
                      <div className="font-semibold">
                        {auditResults.issues.filter(i => i.type === 'warning').length}
                      </div>
                      <div className="text-sm text-muted-foreground">Warnings</div>
                      <div className="text-xs text-primary mt-1">Click to view →</div>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigateToCategory('images')}>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="font-semibold">
                        {auditResults.issues.filter(i => i.type === 'info').length}
                      </div>
                      <div className="text-sm text-muted-foreground">Suggestions</div>
                      <div className="text-xs text-primary mt-1">Click to view →</div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Detailed Results */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="overflow-x-auto">
                  <TabsList className="w-full justify-start min-w-max">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="meta">Meta Tags</TabsTrigger>
                    <TabsTrigger value="images">Images</TabsTrigger>
                    <TabsTrigger value="structure">Structure</TabsTrigger>
                    <TabsTrigger value="performance">Performance</TabsTrigger>
                    <TabsTrigger value="auditor">Live Audit</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="overview" className="space-y-4">
                  <div className="space-y-4">
                    <h3 className="font-semibold">Priority Recommendations</h3>
                    {auditResults.recommendations.map((rec, index) => (
                      <Alert key={index}>
                        <TrendingUp className="h-4 w-4" />
                        <AlertDescription>{rec}</AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </TabsContent>

                {['meta', 'images', 'structure', 'performance'].map(category => (
                  <TabsContent key={category} value={category} className="space-y-4">
                    {issuesByCategory[category]?.map(issue => (
                      <Alert key={issue.id}>
                        {getIssueIcon(issue.type)}
                        <AlertTitle className="flex items-center justify-between">
                          <span>{issue.title}</span>
                          <div className="flex items-center gap-2">
                            {getImpactBadge(issue.impact)}
                            {issue.page && (
                              <Badge variant="outline" className="text-xs">{issue.page}</Badge>
                            )}
                          </div>
                        </AlertTitle>
                        <AlertDescription className="mt-2">
                          <p>{issue.description}</p>
                          {issue.fix && (
                            <p className="text-sm font-medium mt-2">
                              Recommended fix: {issue.fix}
                            </p>
                          )}
                        </AlertDescription>
                      </Alert>
                    )) || (
                      <p className="text-muted-foreground text-center py-8">
                        No issues found in this category. Great job! 🎉
                      </p>
                    )}
                  </TabsContent>
                ))}

                <TabsContent value="auditor" className="space-y-4">
                  <SEOMetaAuditor />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOAuditTool;